import { useId, useRef, useState } from "react";
import type { FormEvent } from "react";
import { ApiError, problemFieldErrors, problemMessage } from "@/api/client";
import type { BulkAnimalRow, BulkCreateAnimalsRequest, BulkCreateAnimalsResponse, Guid, Herd } from "@/api/types";
import { DialogShell } from "./DialogShell";
import "./BulkAnimalsDialog.css";

/**
 * ef-059-8 — "Bulk add": a grid for adding several animals into ONE herd with one request
 * (`POST /api/demo/livestock/animals/bulk`, all or nothing, 1-200 rows).
 *
 * - Five blank starting rows; a row with NO data is skipped, a row with any data must have a tag.
 * - Nothing is ever cleared on an error: client-side checks and a server 400 (`animals[i].field`)
 *   both pin the message to the offending row/field (`aria-invalid` + one `role="alert"` list)
 *   and leave every typed value in place.
 * - ONE Idempotency-Key per open, and a synchronous in-flight guard (a ref, not state) so a double
 *   click sends one request. The key is rotated only after the server REJECTED the body (a 4xx:
 *   nothing was created, and the body is about to change); a network failure keeps it so a retry
 *   of the same body replays instead of double-creating.
 *
 * The shared helpers below (`newIdempotencyKey`, `rowErrorsFrom`) are also
 * used by `CalfCropDialog.tsx`.
 */

const STARTING_ROWS = 5;
const MAX_ROWS = 200;
const TAG_MAX = 50;

type TextField = "tag" | "name" | "sex" | "breed" | "birthDate" | "weight" | "brand" | "color" | "ownerName" | "notes";

interface RowState {
  key: string;
  tag: string;
  name: string;
  sex: string;
  breed: string;
  birthDate: string;
  weight: string;
  brand: string;
  color: string;
  ownerName: string;
  notes: string;
  twin: boolean;
}

/** Column definition: the visible label, the accessible-name stem and the server's field name. */
const COLUMNS: { field: TextField; label: string; server: string; type: "text" | "date" | "number" }[] = [
  { field: "tag", label: "Tag", server: "tag", type: "text" },
  { field: "name", label: "Name", server: "name", type: "text" },
  { field: "sex", label: "Sex", server: "sex", type: "text" },
  { field: "breed", label: "Breed", server: "breed", type: "text" },
  { field: "birthDate", label: "Birth date", server: "birthdate", type: "date" },
  { field: "weight", label: "Weight (lbs)", server: "weightcurrent", type: "number" },
  { field: "brand", label: "Brand", server: "brand", type: "text" },
  { field: "color", label: "Color", server: "color", type: "text" },
  { field: "ownerName", label: "Owner", server: "ownername", type: "text" },
  { field: "notes", label: "Notes", server: "notes", type: "text" },
];

type FieldErrors = Record<string, Partial<Record<TextField | "twin", string>>>;

/** Fresh `Idempotency-Key` — same fallback shape `useLivestock.ts` uses for older iOS Safari. */
export function newIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** A 4xx means the server refused the body and created nothing; 0 (network) and 5xx may have. */
export function wasRejected(err: unknown): boolean {
  return err instanceof ApiError && err.status >= 400 && err.status < 500;
}

/**
 * Splits a 400's `errors` map into per-row field messages and everything else. `prefix` is
 * `animals` or `calves`; keys read `<prefix>[<zero-based row>].<field>`; `rowOf` maps the zero-based
 * index in the REQUEST back to the row in the grid (blank rows are not sent).
 */
export function rowErrorsFrom(
  err: unknown,
  prefix: string,
  rowOf: (index: number) => string | undefined,
  fieldOf: (serverField: string) => string | undefined,
): { rows: Record<string, Record<string, string>>; other: Record<string, string> } | null {
  const errors = problemFieldErrors(err);
  if (!errors) return null;
  const rows: Record<string, Record<string, string>> = {};
  const other: Record<string, string> = {};
  const pattern = new RegExp(`^${prefix}\\[(\\d+)\\]\\.(.+)$`, "i");
  for (const [key, messages] of Object.entries(errors)) {
    const message = messages[0] ?? "Invalid value.";
    const match = pattern.exec(key);
    const rowKey = match ? rowOf(Number(match[1])) : undefined;
    const field = match ? fieldOf(match[2]!.toLowerCase()) : undefined;
    if (match && rowKey && field) {
      rows[rowKey] = { ...rows[rowKey], [field]: message };
    } else {
      other[key.toLowerCase()] = message;
    }
  }
  return { rows, other };
}

function isBlank(row: RowState): boolean {
  return (
    !row.twin &&
    COLUMNS.every((c) => row[c.field].trim() === "")
  );
}

export function BulkAnimalsDialog({
  entityId,
  herds,
  onClose,
  onSubmit,
}: {
  /** `useLivestock`'s `entityId`; `null` blocks submit with a message rather than a guessed id. */
  entityId: Guid | null;
  /** ACTIVE herds only — an archived herd is not a place to file new animals. */
  herds: Herd[];
  /** Cancel, Escape, an overlay click AND after a successful save (same split `AnimalDialog` uses). */
  onClose: () => void;
  /** Throws on failure; reloads the roster on success. */
  onSubmit: (body: BulkCreateAnimalsRequest, idempotencyKey: string) => Promise<BulkCreateAnimalsResponse>;
}) {
  const idPrefix = useId();
  const formId = useId();
  const counter = useRef(0);
  const savingRef = useRef(false);
  const keyRef = useRef(newIdempotencyKey());

  function blankRow(): RowState {
    counter.current += 1;
    return {
      key: `row-${counter.current}`,
      tag: "",
      name: "",
      sex: "",
      breed: "",
      birthDate: "",
      weight: "",
      brand: "",
      color: "",
      ownerName: "",
      notes: "",
      twin: false,
    };
  }

  const [rows, setRows] = useState<RowState[]>(() => Array.from({ length: STARTING_ROWS }, blankRow));
  const [herdId, setHerdId] = useState("");
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [messages, setMessages] = useState<string[]>([]);

  function updateRow(key: string, patch: Partial<RowState>): void {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
    // Editing a field clears ITS error only; the rest stay until the next save attempt.
    const field = Object.keys(patch)[0] as TextField | "twin";
    setFieldErrors((prev) => {
      if (!prev[key]?.[field]) return prev;
      const rest = { ...prev[key] };
      delete rest[field];
      return { ...prev, [key]: rest };
    });
  }

  function rowNumber(key: string): number {
    return rows.findIndex((r) => r.key === key) + 1;
  }

  /** @ui-check: client-side only — mirrors the server's checks so the reason shows without a round trip. */
  function validate(filled: RowState[]): { errors: FieldErrors; lines: string[] } {
    const errors: FieldErrors = {};
    const lines: string[] = [];
    function fail(row: RowState, field: TextField, message: string): void {
      errors[row.key] = { ...errors[row.key], [field]: message };
      lines.push(`Row ${rowNumber(row.key)}: ${message}`);
    }
    for (const row of filled) {
      const tag = row.tag.trim();
      if (tag === "") fail(row, "tag", "Tag is required.");
      else if (tag.length > TAG_MAX) fail(row, "tag", `Tag must be ${TAG_MAX} characters or fewer.`);
      if (row.weight.trim() !== "") {
        const w = Number(row.weight);
        if (!Number.isFinite(w) || w <= 0) fail(row, "weight", "Weight must be a number above 0.");
      }
    }
    return { errors, lines };
  }

  function toRequestRow(row: RowState): BulkAnimalRow {
    const opt = (v: string): string | undefined => (v.trim() === "" ? undefined : v.trim());
    return {
      tag: row.tag.trim(),
      name: opt(row.name),
      sex: opt(row.sex),
      breed: opt(row.breed),
      birthDate: opt(row.birthDate),
      weightCurrent: row.weight.trim() === "" ? undefined : Number(row.weight),
      brand: opt(row.brand),
      color: opt(row.color),
      ownerName: opt(row.ownerName),
      notes: opt(row.notes),
      twin: row.twin ? true : undefined,
    };
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (savingRef.current) return; // synchronous: a double click never gets past here
    setFieldErrors({});
    setMessages([]);

    const filled = rows.filter((r) => !isBlank(r));
    if (filled.length === 0) {
      setMessages(["Enter at least one animal."]);
      return;
    }
    const { errors, lines } = validate(filled);
    if (lines.length > 0) {
      setFieldErrors(errors);
      setMessages(lines);
      return;
    }
    if (entityId === null) {
      setMessages(["No entity found for this tenant yet."]);
      return;
    }

    savingRef.current = true;
    setSaving(true);
    try {
      await onSubmit(
        { entityId, herdId: herdId === "" ? undefined : herdId, animals: filled.map(toRequestRow) },
        keyRef.current,
      );
      onClose();
    } catch (err) {
      if (wasRejected(err)) keyRef.current = newIdempotencyKey(); // nothing was created; the body is about to change
      const mapped = rowErrorsFrom(
        err,
        "animals",
        (i) => filled[i]?.key,
        (serverField) => COLUMNS.find((c) => c.server === serverField)?.field ?? (serverField === "twin" ? "twin" : undefined),
      );
      const lines: string[] = [];
      if (mapped) {
        setFieldErrors(mapped.rows);
        for (const [rowKey, fields] of Object.entries(mapped.rows)) {
          for (const message of Object.values(fields)) lines.push(`Row ${rowNumber(rowKey)}: ${message}`);
        }
        lines.push(...Object.values(mapped.other));
      }
      setMessages(lines.length > 0 ? lines : [problemMessage(err, "Could not save these animals.")]);
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }

  const canAddRow = rows.length < MAX_ROWS;

  return (
    <DialogShell
      title="Bulk add animals"
      size="lg"
      onClose={onClose}
      busy={saving}
      restoreFocus
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" form={formId} className="btn btn--primary" disabled={saving}>
            {saving ? "Saving…" : "Save animals"}
          </button>
        </>
      }
    >
      <form
        id={formId}
        className="entry-dialog__form"
        onSubmit={(e) => void handleSubmit(e)}
        noValidate
        aria-busy={saving}
        data-state={saving ? "saving" : messages.length > 0 ? "error" : "ready"}
      >
        <div className="entry-dialog__field entry-dialog__field--herd">
          <label htmlFor={`${idPrefix}-herd`}>Herd</label>
          <select id={`${idPrefix}-herd`} value={herdId} onChange={(e) => setHerdId(e.target.value)}>
            <option value="">No herd</option>
            {herds.map((h) => (
              <option key={h.id} value={h.id}>
                {h.name}
              </option>
            ))}
          </select>
        </div>

        <p className="entry-dialog__hint">Leave a row empty to skip it. Every row you fill needs a tag.</p>

        <ol className="bulk-rows" aria-label="Animals">
          {rows.map((row, index) => {
            const n = index + 1;
            const errs = fieldErrors[row.key] ?? {};
            return (
              <li
                key={row.key}
                className="bulk-rows__row"
                data-state={Object.keys(errs).length > 0 ? "invalid" : isBlank(row) ? "blank" : "filled"}
              >
                <span className="bulk-rows__number">Row {n}</span>
                {COLUMNS.map((c) => {
                  const id = `${idPrefix}-${row.key}-${c.field}`;
                  return (
                    <div key={c.field} className="entry-dialog__field">
                      <label htmlFor={id} aria-hidden="true">
                        {c.label}
                      </label>
                      <input
                        id={id}
                        aria-label={`${c.label}, row ${n}`}
                        type={c.type}
                        {...(c.type === "number" ? { min: "0", step: "any", inputMode: "decimal" as const } : {})}
                        value={row[c.field]}
                        aria-invalid={errs[c.field] ? true : undefined}
                        onChange={(e) => updateRow(row.key, { [c.field]: e.target.value })}
                      />
                    </div>
                  );
                })}
                <label className="entry-dialog__check">
                  <input
                    type="checkbox"
                    aria-label={`Twin, row ${n}`}
                    checked={row.twin}
                    onChange={(e) => updateRow(row.key, { twin: e.target.checked })}
                  />
                  <span aria-hidden="true">Twin</span>
                </label>
                {rows.length > 1 && (
                  <button
                    type="button"
                    className="btn btn--ghost bulk-rows__remove"
                    aria-label={`Remove row ${n}`}
                    onClick={() => setRows((prev) => prev.filter((r) => r.key !== row.key))}
                  >
                    Remove
                  </button>
                )}
              </li>
            );
          })}
        </ol>

        <button
          type="button"
          className="btn btn--ghost entry-dialog__add"
          disabled={!canAddRow}
          onClick={() => setRows((prev) => [...prev, blankRow()])}
        >
          Add row
        </button>

        {messages.length > 0 && (
          <div className="entry-dialog__error" role="alert">
            {messages.map((m) => (
              <p key={m}>{m}</p>
            ))}
          </div>
        )}

      </form>
    </DialogShell>
  );
}
